import type { ComponentType } from "react";
import { Link } from "react-router-dom";
import { AtSign, UserPlus, Tag } from "lucide-react";
import { CHART_ACCENTS, chartAccentTint, type ChartAccent } from "@/components/charts/chartPalette";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fill } from "@/lib/i18n/fill";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { Language, Translations } from "@/lib/i18n/types";
import { cn } from "@/lib/utils";
import type { ForYouItem } from "./forYou";
import { RECENT_CHANGE_WINDOWS, isRecentChangeWindow, type RecentChangeWindow } from "./summary";
import type { TileSize } from "./tileLayout";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

type Copy = Translations["buildings"]["projectsDashboard"]["recentChanges"];
type IconType = ComponentType<{ className?: string }>;

/** "6 Oct, 14:20" — explicit locale, as elsewhere on the dashboard. */
export function formatWhen(iso: string, language: Language): string {
  return new Date(iso).toLocaleString(language === "et" ? "et-EE" : "en-GB", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** The "Last 72 h" chip on the label row — the same quiet pill as My tasks' "Due within". */
export function TimeframeChip({
  value,
  onChange,
  copy,
}: {
  value: RecentChangeWindow;
  onChange: (value: RecentChangeWindow) => void;
  copy: Copy;
}) {
  return (
    <Select value={value} onValueChange={(next) => isRecentChangeWindow(next) && onChange(next)}>
      <SelectTrigger
        {...pageInfoTarget("dashboard.whatsNewRange")}
        aria-label={copy.timeframeLabel}
        className="h-7 w-auto gap-1.5 rounded-full border-0 bg-[#f0f0f1] px-3 text-xs font-medium text-muted-foreground"
      >
        {/* The trigger's line-clamp drops flex gap — hence the explicit
            non-breaking space (see MyTasksWidget's DueWithinChip). */}
        <span>
          {`${copy.timeframeLabel}\u00A0`}
          <SelectValue />
        </span>
      </SelectTrigger>
      <SelectContent>
        {RECENT_CHANGE_WINDOWS.map((w) => (
          <SelectItem key={w.key} value={w.key} className="text-xs">
            {copy.timeframe[w.key]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * A line's icon on a soft tint of its colour. The colours are the analytics
 * charts' own (`CHART_ACCENTS`), so the dashboard reads as one palette: issues
 * in the rose–indigo–purple range, documents in steel blue and coral, and the
 * brand red kept for what mentions the viewer.
 */
const CHIP_SIZE: Record<TileSize, { box: string; icon: string }> = {
  sm: { box: "h-6 w-6 rounded-md", icon: "h-3.5 w-3.5" },
  md: { box: "h-8 w-8 rounded-lg", icon: "h-4 w-4" },
  lg: { box: "h-10 w-10 rounded-lg", icon: "h-5 w-5" },
};

export function IconChip({
  icon: Icon,
  accent,
  size = "sm",
}: {
  icon: IconType;
  accent: ChartAccent;
  size?: TileSize;
}) {
  return (
    <span
      className={cn("flex flex-shrink-0 items-center justify-center", CHIP_SIZE[size].box)}
      style={{ backgroundColor: chartAccentTint(accent, 0.14), color: CHART_ACCENTS[accent] }}
      aria-hidden
    >
      <Icon className={CHIP_SIZE[size].icon} />
    </span>
  );
}

const FOR_YOU_ICON: Record<ForYouItem["kind"], { icon: IconType; accent: ChartAccent }> = {
  mention: { icon: AtSign, accent: "red" },
  assigned: { icon: UserPlus, accent: "steel" },
  discipline: { icon: Tag, accent: "purple" },
};

export function ForYouRow({ item, copy }: { item: ForYouItem; copy: Copy }) {
  const { language } = useLanguage();
  const { icon, accent } = FOR_YOU_ICON[item.kind];
  const line =
    item.kind === "discipline"
      ? fill(copy.forYou.discipline, { discipline: item.discipline, subject: item.subject })
      : fill(item.kind === "mention" ? copy.forYou.mentioned : copy.forYou.assigned, {
          actor: item.actorName || copy.forYou.someone,
          subject: item.subject,
        });
  return (
    <li>
      <Link
        to={item.href}
        className="flex items-start gap-3 px-4 py-2 text-sm transition-colors hover:bg-[#efeff1]"
      >
        <IconChip icon={icon} accent={accent} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-neutral-800" title={line}>
            {line}
          </span>
          {item.kind === "mention" && item.snippet && (
            <span className="block truncate text-xs text-muted-foreground">{item.snippet}</span>
          )}
        </span>
        <span className="mt-0.5 flex-shrink-0 text-[11px] tabular-nums text-muted-foreground">
          {formatWhen(item.at, language)}
        </span>
      </Link>
    </li>
  );
}
